import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState, useEffect } from 'react';
import { debounce, noop } from 'lodash-es';
import { PusherEventName } from 'anchor-scout-common/types/pusher';
import { TableList, Page, Button, Column, PageEmptyState, PageErrorState, PageContentHeader, PaginationControl, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import TableFilters from '~/components/TableFilters/TableFilters';
import { SearchFilterType } from '~/components/TableFilters/types';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useIntegrationsSummary } from '~/hooks/reactQuery/queries/useIntegrationsSummary';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { useBusinessChannelEvent } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import { useAppConfig } from '~/store/store-context';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '../../components/PageLoadingState';
import SeeWhatItsLike from '../../components/SeeWhatItsLike/SeeWhatItsLike';
import { useFetchPayouts } from './hooks/useFetchPayouts';
import { useUpdatePayoutInvoiceIntegrationCache } from './hooks/useUpdatePayoutInvoiceIntegrationCache';
import PayoutsEmptyStateIcon from './icons/payouts-empty-state.svg';
import styles from './PayoutsDashboard.module.scss';
import { applyFilters, getAccountingStatusFilterOptions, getAccountingStatusFilterOptionsFromPayouts, mapHeaderIdToBackendField, mapHeaderIdToExternalApiSortField, getTableConfig, PayoutsFilterType, payoutsTextsPath } from './PayoutsDashboard.utils';
import PayoutsDashboardRow from './PayoutsDashboardRow/PayoutsDashboardRow';
import img1 from './SeeWhatItsLikeImages/1.png';
import img2 from './SeeWhatItsLikeImages/2.png';
export const GetPayoutsDashboardTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            PayoutsDashboardTID: {
                Empty: 'payouts-empty',
                SeeWhatItsLike: 'payouts-see-what-its-like',
                EmptyNewButton: 'payouts-empty-new-btn',
                Dashboard: 'payouts-dashboard',
                ExportCsv: 'payouts-export-csv',
                Search: 'payouts-dashboard-search',
                TableHeader: 'payouts-dashboard-table-header',
                EmptySearchClearButton: 'payouts-dashboard-empty-search-clear-btn'
            }
        }
    });
export function PayoutsDashboard(props) {
    const { data, isBackgroundLoading, onRefresh, onFilterChange, onSortChange, urlState: externalUrlState, sort: externalSort, pageNumber, paginationPageSize, onPageNumberChange } = props;
    const { api, logBI } = useStoreActions();
    const { PayoutsDashboardTID } = useMemo(()=>GetPayoutsDashboardTID(), []);
    const { t: commonTexts } = useLocaleKeys('common');
    const { PAYOUTS_EXTERNAL_API } = useFeatureFlags();
    const [sort, setSort] = useState({
        colIndex: 1,
        sort: 'desc'
    });
    const scrollElRef = useRef(null);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11401);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const { browser: { downloadCsv } } = useIoContext();
    const { exportCsvFilename } = useAppConfig();
    const { showErrorToast } = useOverlayContext();
    const { t: payoutsTexts } = useLocaleKeys(payoutsTextsPath);
    const { t: payoutsDashboardTexts } = useLocaleKeys('payment.payoutsDashboard');
    const title = payoutsDashboardTexts.title();
    const [payouts, setPayouts] = useState(data.payouts);
    useEffect(()=>{
        setPayouts(data.payouts);
    }, [
        data.payouts
    ]);
    const { urlState: internalUrlState, changeUrlState } = useStateFromUrl({
        qbo: {
            defaultValue: AccountingStatusFilter.All
        },
        xero: {
            defaultValue: AccountingStatusFilter.All
        },
        search: {
            defaultValue: ''
        },
        selected: {
            defaultValue: ''
        },
        payoutId: {
            defaultValue: ''
        }
    });
    const urlState = externalUrlState || internalUrlState;
    const { search, qbo: qboStatusFilter, xero: xeroStatusFilter, selected } = urlState;
    const currentSort = externalSort || sort;
    useOnMount(()=>{
        if (selected) {
            const element = document.querySelector('[data-id="'.concat(selected, '"]'));
            element && requestAnimationFrame(()=>element.scrollIntoView({
                    block: 'center'
                }));
        }
    });
    const updatePayoutFromPusher = (payload)=>{
        var _scrollElRef_current;
        const currentScrollTop = ((_scrollElRef_current = scrollElRef.current) === null || _scrollElRef_current === void 0 ? void 0 : _scrollElRef_current.scrollTop) || 0;
        setPayouts((prev)=>prev.map((prevPayout)=>{
                if (prevPayout.id() === payload.payoutId) {
                    return prevPayout.setIntegrations(payload.integrations);
                }
                return prevPayout;
            }));
        setTimeout(()=>{
            if (scrollElRef.current) {
                scrollElRef.current.scrollTop = currentScrollTop;
            }
        }, 0);
    };
    const updatePayoutInvoiceIntegrationCache = useUpdatePayoutInvoiceIntegrationCache();
    const updatePayoutInvoiceIntegrationFromPusher = (payload)=>{
        var _scrollElRef_current;
        const currentScrollTop = ((_scrollElRef_current = scrollElRef.current) === null || _scrollElRef_current === void 0 ? void 0 : _scrollElRef_current.scrollTop) || 0;
        if (PAYOUTS_EXTERNAL_API) {
            updatePayoutInvoiceIntegrationCache(payload);
        } else {
            setPayouts((prev)=>prev.map((prevPayout)=>{
                    if (prevPayout.id() === payload.payoutId) {
                        return prevPayout.setInvoiceIntegrations(payload.invoiceId, payload.invoiceIntegrations);
                    }
                    return prevPayout;
                }));
        }
        setTimeout(()=>{
            if (scrollElRef.current) {
                scrollElRef.current.scrollTop = currentScrollTop;
            }
        }, 0);
    };
    useBusinessChannelEvent({
        source: 'scout',
        eventName: PusherEventName.PayoutXeroIntegrationStatusUpdated,
        callback: updatePayoutFromPusher
    });
    useBusinessChannelEvent({
        source: 'scout',
        eventName: PusherEventName.PayoutQboIntegrationStatusUpdated,
        callback: updatePayoutFromPusher
    });
    useBusinessChannelEvent({
        source: 'scout',
        eventName: PusherEventName.PayoutInvoiceXeroIntegrationStatusUpdated,
        callback: updatePayoutInvoiceIntegrationFromPusher
    });
    useBusinessChannelEvent({
        source: 'scout',
        eventName: PusherEventName.PayoutInvoiceQboIntegrationStatusUpdated,
        callback: updatePayoutInvoiceIntegrationFromPusher
    });
    const qboStatusFilterOptions = useMemo(()=>{
        if (data.qboSyncStatusFilterCounters) {
            return getAccountingStatusFilterOptions({
                counters: data.qboSyncStatusFilterCounters,
                texts: payoutsTexts.accountingStatusLabels
            });
        }
        return getAccountingStatusFilterOptionsFromPayouts({
            payouts: applyFilters({
                payouts,
                qboStatusFilter: null,
                xeroStatusFilter: null,
                search
            }),
            checkSynced: (payout)=>payout.isQboSynced(),
            texts: payoutsTexts.accountingStatusLabels
        });
    }, [
        data.qboSyncStatusFilterCounters,
        payouts,
        search
    ]);
    const xeroStatusFilterOptions = useMemo(()=>{
        if (data.xeroSyncStatusFilterCounters) {
            return getAccountingStatusFilterOptions({
                counters: data.xeroSyncStatusFilterCounters,
                texts: payoutsTexts.accountingStatusLabels
            });
        }
        return getAccountingStatusFilterOptionsFromPayouts({
            payouts: applyFilters({
                payouts,
                qboStatusFilter: null,
                xeroStatusFilter: null,
                search
            }),
            checkSynced: (payout)=>payout.isXeroSynced(),
            texts: payoutsTexts.accountingStatusLabels
        });
    }, [
        data.xeroSyncStatusFilterCounters,
        payouts,
        search
    ]);
    const { headerItems, columnDefs, sortOptions } = useMemo(()=>getTableConfig({
            showQboIntegration,
            showXeroIntegration,
            texts: payoutsTexts.columnHeaders
        }), [
        showQboIntegration,
        showXeroIntegration
    ]);
    const filterConfig = useMemo(()=>{
        const config = {};
        if (showQboIntegration && qboStatusFilterOptions.length > 0) {
            config[PayoutsFilterType.QboStatus] = {
                type: SearchFilterType.RadioFilter,
                options: qboStatusFilterOptions.map((opt)=>({
                        value: opt.value,
                        label: opt.label,
                        sublabel: "(".concat(opt.counter, ")")
                    })),
                removable: false,
                defaultOption: AccountingStatusFilter.All
            };
        }
        if (showXeroIntegration && xeroStatusFilterOptions.length > 0) {
            config[PayoutsFilterType.XeroStatus] = {
                type: SearchFilterType.RadioFilter,
                options: xeroStatusFilterOptions.map((opt)=>({
                        value: opt.value,
                        label: opt.label,
                        sublabel: "(".concat(opt.counter, ")")
                    })),
                removable: false,
                defaultOption: AccountingStatusFilter.All
            };
        }
        return config;
    }, [
        showQboIntegration,
        showXeroIntegration,
        qboStatusFilterOptions,
        xeroStatusFilterOptions
    ]);
    const filterLabels = useMemo(()=>{
        const labels = {};
        if (showQboIntegration) {
            labels[PayoutsFilterType.QboStatus] = payoutsDashboardTexts.qboSyncStatus();
        }
        if (showXeroIntegration) {
            labels[PayoutsFilterType.XeroStatus] = payoutsDashboardTexts.xeroSyncStatus();
        }
        return labels;
    }, [
        showQboIntegration,
        showXeroIntegration
    ]);
    const tableSelectedFilters = useMemo(()=>{
        const next = {};
        if (filterConfig[PayoutsFilterType.QboStatus]) next[PayoutsFilterType.QboStatus] = qboStatusFilter;
        if (filterConfig[PayoutsFilterType.XeroStatus]) next[PayoutsFilterType.XeroStatus] = xeroStatusFilter;
        return next;
    }, [
        filterConfig,
        qboStatusFilter,
        xeroStatusFilter
    ]);
    const sortedAndFiltered = useMemo(()=>{
        return payouts;
    }, [
        payouts
    ]);
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: sortedAndFiltered,
        scrollElRef,
        alwaysRenderIndex: selected ? (data)=>data.findIndex((p)=>p.id() === selected) : undefined
    });
    function handleSortChange(sort) {
        logBI(11400, {
            column: sort ? headerItems[sort.colIndex].value : 'none'
        });
        if (onSortChange) {
            onSortChange(sort);
        } else {
            setSort(sort);
        }
    }
    function onClearSearch() {
        if (onFilterChange) {
            onFilterChange({
                search: ''
            });
        } else {
            changeUrlState({
                search: ''
            });
        }
        debouncedLogSearchToBI.onClear();
    }
    async function exportToCsv() {
        var _headerItems_currentSort_colIndex;
        const sortField = currentSort ? (_headerItems_currentSort_colIndex = headerItems[currentSort.colIndex]) === null || _headerItems_currentSort_colIndex === void 0 ? void 0 : _headerItems_currentSort_colIndex.id : undefined;
        const sortType = currentSort === null || currentSort === void 0 ? void 0 : currentSort.sort;
        const result = await handlePromise(api.payout.exportPayoutsToCsv({
            qboIntegration: qboStatusFilter,
            xeroIntegration: xeroStatusFilter,
            sort: sortField && sortType ? {
                field: mapHeaderIdToBackendField(sortField),
                type: sortType
            } : undefined,
            search
        }));
        if (result.success) {
            const filename = exportCsvFilename || 'exported-payouts.csv';
            downloadCsv(result.data, filename);
        } else {
            showErrorToast(commonTexts.toasts.errorExportingPayoutsToCsv(), 'export-payouts-error');
        }
    }
    const hasPayoutsOrFiltersApplied = payouts.length > 0 || search || qboStatusFilter !== AccountingStatusFilter.All || xeroStatusFilter !== AccountingStatusFilter.All;
    const renderEmptyPage = ()=>/*#__PURE__*/ _jsx(Page, {
            header: title,
            loading: isBackgroundLoading,
            variant: "containedHeight",
            onRefresh: onRefresh,
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                maxHeight: true,
                testId: PayoutsDashboardTID.Empty(),
                illustration: /*#__PURE__*/ _jsx(PayoutsEmptyStateIcon, {}),
                title: payoutsTexts.emptyState.title(),
                message: payoutsTexts.emptyState.message(),
                actions: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SeeWhatItsLike, {
                            steps: [
                                {
                                    title: payoutsDashboardTexts.seeWhatItsLike.step1Title(),
                                    subtitle: payoutsDashboardTexts.seeWhatItsLike.step1Subtitle(),
                                    image: {
                                        src: img1,
                                        width: 1198,
                                        height: 700
                                    }
                                },
                                {
                                    title: payoutsDashboardTexts.getPaidOnTime(),
                                    subtitle: payoutsDashboardTexts.seeWhatItsLike.step2Subtitle(),
                                    image: {
                                        src: img2,
                                        width: 1246,
                                        height: 748
                                    }
                                }
                            ],
                            testId: PayoutsDashboardTID.SeeWhatItsLike()
                        }),
                        /*#__PURE__*/ _jsx(CreateProposalButton, {
                            testId: PayoutsDashboardTID.EmptyNewButton()
                        }, "create-proposal-button")
                    ]
                })
            })
        });
    const renderPayoutsPage = ()=>/*#__PURE__*/ _jsx(Page, {
            testId: PayoutsDashboardTID.Dashboard(),
            ref: scrollElRef,
            onRefresh: onRefresh,
            header: title,
            loading: isBackgroundLoading,
            actions: /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'export_payouts_csv'
                },
                testId: PayoutsDashboardTID.ExportCsv(),
                onClick: exportToCsv,
                children: payoutsDashboardTexts.exportToCsv()
            }),
            subheader: payoutsDashboardTexts.subheader(),
            onScroll: onScroll,
            children: /*#__PURE__*/ _jsxs(TableList.Provider, {
                columnDefs: columnDefs,
                expandableRows: true,
                initialExpandedRows: selected ? {
                    [selected]: true
                } : {},
                singleExpandOnly: true,
                sort: currentSort,
                onSortChange: handleSortChange,
                children: [
                    /*#__PURE__*/ _jsx(PageContentHeader, {
                        gapBelow: "rows",
                        children: /*#__PURE__*/ _jsxs(Column, {
                            gap: 16,
                            children: [
                                /*#__PURE__*/ _jsx(TableFilters, {
                                    testIdPrefix: "payouts",
                                    search: {
                                        bi: {
                                            name: 'payouts_search'
                                        },
                                        value: search,
                                        testId: PayoutsDashboardTID.Search(),
                                        registerHotkey: true,
                                        onChange: (event)=>{
                                            const searchStr = event.target.value;
                                            if (onFilterChange) {
                                                onFilterChange({
                                                    search: searchStr
                                                }, {
                                                    withDebounce: true
                                                });
                                            } else {
                                                changeUrlState({
                                                    search: searchStr
                                                });
                                            }
                                            debouncedLogSearchToBI.onChange(searchStr);
                                        },
                                        onClear: onClearSearch
                                    },
                                    filterLabels: filterLabels,
                                    filterConfig: filterConfig,
                                    selectedFilters: tableSelectedFilters,
                                    setSelectedFilters: noop,
                                    mobile: {
                                        sortOptions
                                    },
                                    onRefresh: (filters)=>{
                                        var _filters_PayoutsFilterType_QboStatus, _filters_PayoutsFilterType_XeroStatus;
                                        const newQbo = (_filters_PayoutsFilterType_QboStatus = filters[PayoutsFilterType.QboStatus]) !== null && _filters_PayoutsFilterType_QboStatus !== void 0 ? _filters_PayoutsFilterType_QboStatus : AccountingStatusFilter.All;
                                        const newXero = (_filters_PayoutsFilterType_XeroStatus = filters[PayoutsFilterType.XeroStatus]) !== null && _filters_PayoutsFilterType_XeroStatus !== void 0 ? _filters_PayoutsFilterType_XeroStatus : AccountingStatusFilter.All;
                                        const filtersChanged = newQbo !== qboStatusFilter || newXero !== xeroStatusFilter;
                                        if (!filtersChanged) return;
                                        if (onFilterChange) {
                                            onFilterChange({
                                                qbo: newQbo,
                                                xero: newXero
                                            });
                                        } else {
                                            changeUrlState({
                                                qbo: newQbo,
                                                xero: newXero
                                            });
                                        }
                                    }
                                }),
                                /*#__PURE__*/ _jsx(TableList.Header, {
                                    items: headerItems,
                                    testId: PayoutsDashboardTID.TableHeader()
                                })
                            ]
                        })
                    }),
                    sortedAndFiltered.length ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            paginatedData.map((payout, index)=>/*#__PURE__*/ _jsx(PayoutsDashboardRow, {
                                    payout: payout,
                                    index: index,
                                    invoiceHeaders: data.invoiceHeaders
                                }, payout.id())),
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.paginationControl,
                                children: /*#__PURE__*/ _jsx(PaginationControl, {
                                    pageSize: paginationPageSize || 100,
                                    pageNumber: pageNumber || 1,
                                    total: data.total,
                                    nextLabel: commonTexts.pagination.next(),
                                    previousLabel: commonTexts.pagination.previous(),
                                    viewingLabel: commonTexts.pagination.viewing,
                                    onPageNumberChange: onPageNumberChange || (()=>{})
                                })
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx(PageEmptyState, {
                        illustration: /*#__PURE__*/ _jsx(PayoutsEmptyStateIcon, {}),
                        title: payoutsTexts.noResults.title(),
                        message: payoutsTexts.noResults.message(),
                        actions: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'clear_payouts_search'
                            },
                            testId: PayoutsDashboardTID.EmptySearchClearButton(),
                            onClick: onClearSearch,
                            children: payoutsDashboardTexts.clearSearch()
                        })
                    })
                ]
            })
        });
    return hasPayoutsOrFiltersApplied ? renderPayoutsPage() : renderEmptyPage();
}
const PAYOUTS_PAGE_SIZE = 50;
function ScoutPayoutsDashboard() {
    var _headerItems_sort_colIndex;
    const { t: payoutsDashboardTexts } = useLocaleKeys('payment.payoutsDashboard');
    const title = payoutsDashboardTexts.title();
    useSetDocumentTitle(title);
    const { inputDebounceTime } = useAppConfig();
    const { refresh: refreshIntegrationsSummary } = useIntegrationsSummary({
        enabled: false
    });
    const { PAYOUTS_EXTERNAL_API } = useFeatureFlags();
    const [pageNumber, setPageNumber] = useState(1);
    const { t: payoutsTexts } = useLocaleKeys(payoutsTextsPath);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const { headerItems } = useMemo(()=>getTableConfig({
            showQboIntegration,
            showXeroIntegration,
            texts: payoutsTexts.columnHeaders
        }), [
        showQboIntegration,
        showXeroIntegration
    ]);
    const sortIds = useMemo(()=>headerItems.map((item)=>{
            var _item_id;
            return (_item_id = item.id) !== null && _item_id !== void 0 ? _item_id : '';
        }), [
        headerItems
    ]);
    const defaultSort = useMemo(()=>({
            colIndex: 1,
            sort: 'desc'
        }), []);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const { urlState, changeUrlState } = useStateFromUrl({
        qbo: {
            defaultValue: AccountingStatusFilter.All
        },
        xero: {
            defaultValue: AccountingStatusFilter.All
        },
        search: {
            defaultValue: ''
        },
        selected: {
            defaultValue: ''
        },
        payoutId: {
            defaultValue: ''
        }
    });
    const [debouncedSearch, setDebouncedSearch] = useState(urlState.search);
    const debouncedSetSearch = useMemo(()=>debounce((next)=>setDebouncedSearch(next), inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500), []);
    useEffect(()=>{
        if (!urlState.search) {
            debouncedSetSearch.cancel();
            setDebouncedSearch('');
            return;
        }
        debouncedSetSearch(urlState.search);
        return ()=>debouncedSetSearch.cancel();
    }, [
        urlState.search,
        debouncedSetSearch
    ]);
    const sortField = sort ? (_headerItems_sort_colIndex = headerItems[sort.colIndex]) === null || _headerItems_sort_colIndex === void 0 ? void 0 : _headerItems_sort_colIndex.id : undefined;
    const sortType = sort === null || sort === void 0 ? void 0 : sort.sort;
    const mapSortField = PAYOUTS_EXTERNAL_API ? mapHeaderIdToExternalApiSortField : mapHeaderIdToBackendField;
    const resolvedSort = useMemo(()=>sortField && sortType ? {
            field: mapSortField(sortField),
            type: sortType
        } : undefined, [
        sortField,
        sortType,
        mapSortField
    ]);
    const { data, isLoading, isBackgroundLoading, error, refresh } = useFetchPayouts({
        qboIntegration: urlState.qbo,
        xeroIntegration: urlState.xero,
        search: debouncedSearch,
        pageSize: PAYOUTS_PAGE_SIZE,
        pageNumber,
        sort: resolvedSort,
        useExternalApi: !!PAYOUTS_EXTERNAL_API
    }, {
        refreshOnHookMount: true
    });
    useEffect(()=>{
        if (showQboIntegration || showXeroIntegration) {
            refreshIntegrationsSummary({
                throwOnError: false
            });
        }
    }, [
        debouncedSearch,
        urlState.qbo,
        urlState.xero,
        resolvedSort,
        pageNumber,
        refreshIntegrationsSummary,
        showQboIntegration,
        showXeroIntegration
    ]);
    function handleFilterChange(newUrlState) {
        changeUrlState(newUrlState);
        setPageNumber(1);
    }
    function handleSortChange(newSort) {
        changeSort(newSort);
        setPageNumber(1);
    }
    if (isLoading) return /*#__PURE__*/ _jsx(PageLoadingState, {
        header: title,
        loading: true
    });
    if (error || !data) return /*#__PURE__*/ _jsx(PageErrorState, {
        title: title,
        error: error,
        onRefresh: ()=>refresh(),
        pullToRefresh: true
    });
    return /*#__PURE__*/ _jsx(PayoutsDashboard, {
        data: data,
        isBackgroundLoading: isBackgroundLoading,
        onRefresh: refresh,
        onFilterChange: handleFilterChange,
        onSortChange: handleSortChange,
        onPageNumberChange: setPageNumber,
        onResetFilters: ()=>handleFilterChange({
                qbo: AccountingStatusFilter.All,
                xero: AccountingStatusFilter.All,
                search: ''
            }),
        urlState: urlState,
        sort: sort,
        pageNumber: pageNumber,
        paginationPageSize: PAYOUTS_PAGE_SIZE
    });
}
export default ScoutPayoutsDashboard;
